import { PlusOutlined } from "@ant-design/icons";
import { Button, Col, Row, Typography } from "antd";
import type { TFunction } from "i18next";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAuth } from "@/modules/auth";
import { RecurringFeeBlock } from "@/modules/recurring-fee";
import { useCustomerSearch } from "@/lib/customerSearch/useCustomerSearch";
import api from "../../../api";
import { useTaxServicesQuery } from "../../../api/graphql/queries/taxService/taxServices.generated";
import { IUser } from "../../../types";
import { destroyMessage, showLoadingMessage } from "../../../utils";
import { ContentWrapper } from "../../common/styledComponents";
import useEmailParam from "../../hooks/useEmailParam";
import UserFilters, {
  UserFilterOptions,
} from "../Mandanten/Common/UserFilters";
import CreateTaxServiceModal from "./createTaxServiceModal";
import { TaxServiceContainer } from "./styledComponent";
import TaxServicesTable from "./TaxServicesTable";

const { Title, Text } = Typography;

const LOADING_MESSAGE_KEY = "finding-user";

const getTexts = (t: TFunction<"ServiceTracker", undefined>) => ({
  headline: t("serviceTracker.headline"),
  noClientSelectedWarning: t("serviceTracker.noClientSelectedWarning"),
  ctaAddNewService: t("serviceTracker.ctaAddNewService"),
});

const TaxServiceView = () => {
  const { t } = useTranslation("ServiceTracker");
  const texts = getTexts(t);

  const { getOperatorEmail } = useAuth();
  const operatorEmail = getOperatorEmail();

  const [emailParam, setEmailParam] = useEmailParam();
  const [searchParam, setSearchParam] = useState<string | null | undefined>(
    emailParam
  );
  const [filterOptions, setFilterOptions] = useState<UserFilterOptions>({
    search: searchParam || "",
    all: false,
    myCases: false,
  });
  const [user, setUser] = useState<IUser | null>(null);
  const [showCreateTaxServiceModal, setShowCreateTaxServiceModal] =
    useState(false);

  const { data: queryResult, refetch: refetchTaxServices } =
    useTaxServicesQuery({
      skip: !filterOptions.search.length && !filterOptions.myCases,
      variables: {
        filter: {
          email: filterOptions.search,
          createdBy: filterOptions.myCases ? operatorEmail : "",
        },
      },
      fetchPolicy: "network-only",
      nextFetchPolicy: "cache-first",
    });
  const taxServices = queryResult?.taxServices;

  const fetchUser = useCallback(async (email: string) => {
    if (!email) {
      return;
    }
    showLoadingMessage(LOADING_MESSAGE_KEY);
    try {
      const { user } = await api.kontax.getVatMeta(email);
      setUser(user);
    } catch (error) {
      setUser(null);
    } finally {
      destroyMessage(LOADING_MESSAGE_KEY);
    }
  }, []);

  const selectEmail = useCallback(
    (email: string | undefined) => setEmailParam(email),
    [setEmailParam]
  );
  const { search: searchCustomer } = useCustomerSearch(selectEmail);

  useEffect(() => {
    if (emailParam) {
      fetchUser(emailParam);
    } else {
      setUser(null);
    }
  }, [emailParam, fetchUser]);

  return (
    <ContentWrapper>
      <UserFilters
        withoutLoadAll
        withMyCases
        filterOptions={filterOptions}
        setFilterOptions={(
          updatedFilterOptions: Partial<UserFilterOptions>
        ) => {
          if (!updatedFilterOptions.all) {
            setSearchParam(updatedFilterOptions.search);
            // Selects the customer by their real email also for a Public ID
            // or Account ID; zero or several matches select nobody (SMT-1396).
            searchCustomer(updatedFilterOptions.search || "");
          }
          setFilterOptions({
            ...filterOptions,
            ...updatedFilterOptions,
          });
        }}
      />
      <TaxServiceContainer>
        <Title level={3}>{texts.headline}</Title>
        {!user && <Text type="secondary">{texts.noClientSelectedWarning}</Text>}
        {user && <RecurringFeeBlock key={user.email} email={user.email} />}
        <Row justify="end">
          <Col>
            <Button
              icon={<PlusOutlined />}
              type="primary"
              disabled={!user}
              onClick={() => {
                setShowCreateTaxServiceModal(true);
              }}
            >
              {texts.ctaAddNewService}
            </Button>
          </Col>
        </Row>
        <br />
        <Row>
          <TaxServicesTable
            taxServices={taxServices}
            onRefresh={refetchTaxServices}
          />
        </Row>
        <CreateTaxServiceModal
          user={user}
          visible={showCreateTaxServiceModal}
          onClose={() => {
            setShowCreateTaxServiceModal(false);
          }}
          onSuccess={refetchTaxServices}
        />
      </TaxServiceContainer>
    </ContentWrapper>
  );
};

export default TaxServiceView;
